/**
 * TemplateEntitySlots
 *
 * Coverage entity slot selector for template instantiation.
 * Fetches coverages for a matter and groups them by slot type (1P, 3P, Health).
 * Users select which coverage fills each slot for template variable population.
 *
 * Similar to ComponentCoverageSelector but simplified for the templates-v2 flow.
 */

import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useGetCoveragesQuery } from '@/services/api/coverageService';
import {
  CoverageCategory,
  type CoverageSchema,
  IdentifierType,
} from '@/services/types/coverage-types';

/**
 * Coverage slot names that correspond to template variable categories.
 */
export type EntitySlotName = '1p' | '3p' | 'health';

/**
 * Map of slot name to selected coverage ID.
 */
export type EntitySlotSelections = Partial<Record<EntitySlotName, string>>;

interface TemplateEntitySlotsProps {
  /** The matter ID to fetch coverages for */
  matterId: string;
  /** Current slot selections */
  selections?: EntitySlotSelections;
  /** Callback when a slot selection changes */
  onSlotChange: (slotName: EntitySlotName, coverageId: string | null) => void;
  /** Optional className for the container */
  className?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
}

/**
 * Maps coverage categories to their corresponding slot names.
 */
export const CATEGORY_TO_SLOT: Record<CoverageCategory, EntitySlotName | null> = {
  [CoverageCategory.FIRST_PARTY]: '1p',
  [CoverageCategory.THIRD_PARTY]: '3p',
  [CoverageCategory.FIRST_PARTY_HEALTH]: 'health',
  [CoverageCategory.NOT_LIABLE_INVOLVED_PARTY]: null,
  [CoverageCategory.UNCLEAR]: null,
  [CoverageCategory.PROPERTY]: null,
  [CoverageCategory.WORKERS_COMPENSATION]: null,
};

/**
 * Display configuration for each slot.
 */
const SLOT_CONFIG: Record<
  EntitySlotName,
  { label: string; description: string; emptyText: string }
> = {
  '1p': {
    label: '1P Coverage',
    description: "Client's auto insurance (MedPay, PIP, UM/UIM)",
    emptyText: 'No 1P coverage available',
  },
  '3p': {
    label: '3P Coverage',
    description: "At-fault party's insurance (Bodily Injury)",
    emptyText: 'No 3P coverage available',
  },
  health: {
    label: 'Health Coverage',
    description: "Client's health insurance",
    emptyText: 'No health coverage available',
  },
};

const SLOT_ORDER: EntitySlotName[] = ['1p', '3p', 'health'];

export const getCoverageDisplayName = (coverage: CoverageSchema): string =>
  coverage.carrier?.name || 'Unknown Carrier';

export const getPolicyNumber = (coverage: CoverageSchema): string | null => {
  if (!coverage.policy_identifiers || coverage.policy_identifiers.length === 0) {
    return null;
  }
  const policyNumber = coverage.policy_identifiers.find(
    (id) => id.identifier_type === IdentifierType.POLICY_NUMBER,
  );
  return (policyNumber || coverage.policy_identifiers[0])?.identifier_value || null;
};

/* ---------- SlotSelector ---------- */

export interface SlotSelectorProps {
  slotName: EntitySlotName;
  coverages: CoverageSchema[];
  selectedCoverageId: string | null;
  onSelect: (coverageId: string | null) => void;
  disabled: boolean;
}

export const SlotSelector: React.FC<SlotSelectorProps> = ({
  slotName,
  coverages,
  selectedCoverageId,
  onSelect,
  disabled,
}) => {
  const config = SLOT_CONFIG[slotName];
  const selectedCoverage = coverages.find((c) => c.id === selectedCoverageId);
  const hasOptions = coverages.length > 0;
  const selectId = `entity-slot-${slotName}`;

  const handleValueChange = useCallback(
    (value: string) => {
      onSelect(value === '__none__' ? null : value);
    },
    [onSelect],
  );

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <span
          id={`${selectId}-label`}
          className="text-xs font-semibold text-zinc-700 dark:text-zinc-300"
        >
          {config.label}
        </span>
        {selectedCoverage && (
          <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30">
            Selected
          </span>
        )}
      </div>
      <p className="text-[11px] font-medium text-zinc-500 leading-tight">
        {config.description}
      </p>

      <Select
        value={selectedCoverageId || '__none__'}
        onValueChange={handleValueChange}
        disabled={disabled || !hasOptions}
      >
        <SelectTrigger
          aria-labelledby={`${selectId}-label`}
          className={cn(
            'w-full h-9 text-sm',
            !hasOptions && 'text-zinc-500',
            selectedCoverage && 'border-indigo-200 bg-indigo-50/30 dark:border-indigo-500/30 dark:bg-indigo-500/10',
          )}
        >
          <SelectValue placeholder={config.emptyText}>
            {selectedCoverage && (
              <span className="flex items-center gap-2 truncate">
                <ShieldCheckIcon className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                <span className="truncate">
                  {getCoverageDisplayName(selectedCoverage)}
                </span>
              </span>
            )}
            {!selectedCoverage && hasOptions && 'Select a coverage...'}
            {!selectedCoverage && !hasOptions && config.emptyText}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {hasOptions && (
            <SelectItem value="__none__" className="text-zinc-500">
              No selection
            </SelectItem>
          )}
          {coverages.map((coverage) => {
            const policyNumber = getPolicyNumber(coverage);
            const displayName = getCoverageDisplayName(coverage);

            return (
              <SelectItem key={coverage.id} value={coverage.id} className="py-2">
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium">{displayName}</span>
                  {policyNumber && (
                    <span className="text-[11px] font-medium text-zinc-500">
                      Policy: {policyNumber}
                    </span>
                  )}
                  {!policyNumber && (
                    <span className="text-[11px] font-medium text-zinc-500 italic">
                      No policy info
                    </span>
                  )}
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

/* ---------- TemplateEntitySlots ---------- */

export const TemplateEntitySlots: React.FC<TemplateEntitySlotsProps> = ({
  matterId,
  selections = {},
  onSlotChange,
  className,
  disabled = false,
}) => {
  const { data: coverages, isLoading, error } = useGetCoveragesQuery(
    { matterId },
    { skip: !matterId },
  );

  // Group coverages by slot
  const coveragesBySlot = useMemo(() => {
    const result: Record<EntitySlotName, CoverageSchema[]> = {
      '1p': [],
      '3p': [],
      health: [],
    };

    if (!coverages) return result;

    coverages.forEach((coverage) => {
      const slotName = coverage.category ? CATEGORY_TO_SLOT[coverage.category] : null;
      if (slotName) {
        result[slotName].push(coverage);
      }
    });

    return result;
  }, [coverages]);

  // Only show slots that have available coverages
  const visibleSlots = useMemo(
    () => SLOT_ORDER.filter((slotName) => coveragesBySlot[slotName].length > 0),
    [coveragesBySlot],
  );

  const handleSlotChange = useCallback(
    (slotName: EntitySlotName) => (coverageId: string | null) => {
      onSlotChange(slotName, coverageId);
    },
    [onSlotChange],
  );

  if (!matterId) return null;

  if (isLoading) {
    return (
      <div className={cn('space-y-4', className)}>
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="h-4 w-4 text-zinc-400" />
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
            Entity Slots
          </h3>
        </div>
        <EntitySlotsSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="h-4 w-4 text-zinc-400" />
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
            Entity Slots
          </h3>
        </div>
        <div className="text-sm font-medium text-red-600 dark:text-red-400">
          Failed to load coverages. Please try again.
        </div>
      </div>
    );
  }

  if (visibleSlots.length === 0) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="h-4 w-4 text-zinc-400" />
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
            Entity Slots
          </h3>
        </div>
        <div className="text-sm font-medium text-zinc-500 py-4 text-center border border-dashed border-zinc-200 dark:border-zinc-700 rounded-lg">
          No coverages found for this matter.
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <ShieldCheckIcon className="h-4 w-4 text-zinc-400" />
        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
          Entity Slots
        </h3>
      </div>

      {/* Slot selectors */}
      <div className="space-y-4">
        {visibleSlots.map((slotName) => (
          <SlotSelector
            key={slotName}
            slotName={slotName}
            coverages={coveragesBySlot[slotName]}
            selectedCoverageId={selections[slotName] ?? null}
            onSelect={handleSlotChange(slotName)}
            disabled={disabled}
          />
        ))}
      </div>

      {/* Tip */}
      <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800">
        <p className="text-[11px] font-medium text-zinc-500">
          <span className="font-semibold text-zinc-600 dark:text-zinc-400">Tip:</span>{' '}
          Selected coverages populate insurance template variables (e.g.,{' '}
          <code className="text-[10px] px-1 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded font-mono">
            insurance1pCarrier
          </code>
          ).
        </p>
      </div>
    </div>
  );
};

/* ---------- Skeleton ---------- */

const EntitySlotsSkeleton = () => (
  <div className="space-y-4">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="space-y-1.5">
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-3 w-48" />
        <Skeleton className="h-9 w-full rounded-md" />
      </div>
    ))}
  </div>
);
